import React, { useEffect, useState } from 'react';
import { useSente } from '../hooks/useSente';

// Plays a finished run's Hyperbrowser video. The URL is ready only after HB finishes processing once
// the session ends, so poll while status is pending/in_progress. 'completed' → play; 'failed' /
// 'not_enabled' → nothing to show. Same aspect-video box as the live viewer so layouts don't jump.
const RunRecording: React.FC<{ runId: string }> = ({ runId }) => {
  const { ready, getRunRecording } = useSente();
  const [status, setStatus] = useState('pending');
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const rec = await getRunRecording(runId);
        if (cancelled) return;
        setStatus(rec.status);
        setUrl(rec.recordingUrl);
        // Keep polling only while HB is still producing the file.
        if (rec.status === 'pending' || rec.status === 'in_progress') timer = setTimeout(poll, 4000);
      } catch {
        if (!cancelled) setStatus('failed');
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ready, runId, getRunRecording]);

  if (status === 'completed' && url) {
    return (
      <video
        src={url}
        controls
        playsInline
        className="w-full aspect-video rounded-xl border border-gray-200 dark:border-slate-800 bg-black"
      />
    );
  }

  const done = status === 'failed' || status === 'not_enabled';
  return (
    <div className="w-full aspect-video flex items-center justify-center gap-3 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 text-sm">
      {!done && <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full" />}
      {done ? 'No recording available for this run.' : 'Preparing the run recording…'}
    </div>
  );
};

export default RunRecording;
